import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { COLOR } from '@fe/common/src/constants/main'
import { VopMatchResult } from '@fe/common/types/VopTypes'

const WarningContainer = styled.div<{
  $variant: 'warning' | 'danger' | 'info'
}>`
  padding: 1rem 1.5rem;
  margin-bottom: 1.5rem;
  border-radius: 0.5rem;
  background-color: ${props => {
    switch (props.$variant) {
      case 'danger':
        return COLOR.RED_LIGHT
      case 'warning':
        return COLOR.YELLOW_LIGHT
      case 'info':
        return COLOR.BLUE_LIGHT
    }
  }};
  border-left: 4px solid
    ${props => {
      switch (props.$variant) {
        case 'danger':
          return COLOR.RED
        case 'warning':
          return COLOR.YELLOW
        case 'info':
          return COLOR.BLUE
      }
    }};
`

const WarningTitle = styled.h4`
  margin: 0 0 0.5rem 0;
  font-weight: 600;
  font-size: 1rem;
`

const WarningText = styled.p`
  margin: 0.5rem 0;
  line-height: 1.5;
`

const SuggestedName = styled.div`
  margin: 1rem 0;
  padding: 0.75rem;
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 0.25rem;
  font-weight: 600;
`

interface VopWarningProps {
  matchResult: VopMatchResult | null
  suggestedName?: string
  error?: string
  isVerifying: boolean
}

export const VopWarning: React.FC<VopWarningProps> = ({
  matchResult,
  suggestedName,
  error,
  isVerifying,
}) => {
  const { t } = useTranslation('transactions')

  if (isVerifying) {
    return (
      <WarningContainer $variant="info">
        <WarningText>{t('vop-verifying')}</WarningText>
      </WarningContainer>
    )
  }

  if (error) {
    return (
      <WarningContainer $variant="warning">
        <WarningTitle>{t('vop-error-title')}</WarningTitle>
        <WarningText>{t('vop-error')}</WarningText>
        <WarningText style={{ fontSize: '0.875rem', opacity: 0.8 }}>
          {error}
        </WarningText>
      </WarningContainer>
    )
  }

  if (matchResult === VopMatchResult.Match) {
    return null
  }

  if (matchResult === VopMatchResult.CloseMatch) {
    return (
      <WarningContainer $variant="warning">
        <WarningTitle>{t('vop-close-match-title')}</WarningTitle>
        <WarningText>{t('vop-close-match-warning')}</WarningText>
        {suggestedName && (
          <>
            <WarningText>
              <strong>{t('vop-suggested-name')}:</strong>
            </WarningText>
            <SuggestedName>{suggestedName}</SuggestedName>
          </>
        )}
      </WarningContainer>
    )
  }

  if (matchResult === VopMatchResult.NoMatch) {
    return (
      <WarningContainer $variant="danger">
        <WarningTitle>{t('vop-no-match-title')}</WarningTitle>
        <WarningText>{t('vop-no-match-warning')}</WarningText>
      </WarningContainer>
    )
  }

  return null
}
